import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  $httpRootApi,
  ErrorWithTrace,
  UpdateCacheCallback,
  updateQueryCache,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@core/http-client';
import { getQueriesAsSearch } from '@shared/utils/common';
import { serializeBodyToFormData } from '@shared/utils/formdata-serializer';

import { FeeShowItem, FeeUpdatePayload } from './feesApi.types';
import {
  RequirementAddToLonglistParams,
  RequirementAddToLonglistResponse,
  RequirementAddToShortlistParams,
  RequirementAddToShortlistResponse,
  RequirementArchivePayload,
  RequirementAssociateCompanyParams,
  RequirementAssociateContactParams,
  RequirementAvailabilityScheduleListPayload,
  RequirementAvailabilityScheduleListResponse,
  RequirementCreateCommentPayload,
  RequirementDeleteCompaniesPayload,
  RequirementDeleteContactsPayload,
  RequirementDeleteFilePayload,
  RequirementMatchesIndexResponse,
  RequirementMatchesPayload,
  RequirementMatchesResponse,
  RequirementReassignPayload,
  RequirementRecordLettingTransactionPayload,
  RequirementRecordSaleTransactionPayload,
  RequirementRemoveTeamsPayload,
  RequirementRenameFilePayload,
  RequirementReorderContactsParams,
  RequirementReorderFilesPayload,
  RequirementsBoardLaneKey,
  RequirementsBoardLanePayload,
  RequirementsBoardLaneResponse,
  RequirementsBoardPayload,
  RequirementsBoardResponse,
  RequirementSendMatchesPayload,
  RequirementShortlistResponse,
  RequirementShowItem,
  RequirementsListParams,
  RequirementsListPayload,
  RequirementsListResponse,
  RequirementTransactionsResponse,
  RequirementUpdateCompaniesPayload,
  RequirementUpdateContactsPayload,
  RequirementUpdatePayload,
} from './requirementsApi.types';

export const requirementQueries = {
  list: async (payload: RequirementsListPayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.get<RequirementsListResponse>(`/acquisitions${getQueriesAsSearch(payload.query)}`, config),

  show: async (id: string, config?: AxiosRequestConfig) => await $httpRootApi.get(`/acquisitions/${id}`, config),
};

export const requirementsKeys = createQueryKeys('requirements', {
  all: null,
  list: (payload: RequirementsListPayload) => ({
    queryKey: [payload.query],
    queryFn: async () => (await requirementQueries.list(payload)).data,
  }),
  board: (payload: RequirementsBoardPayload) => ({
    queryKey: [payload.query],
    queryFn: async () =>
      (await $httpRootApi.get<RequirementsBoardResponse>(`/acquisitions/board${getQueriesAsSearch(payload.query)}`))
        .data,
  }),
  boardLane: (payload: RequirementsBoardLanePayload) => ({
    queryKey: [payload.laneKey || '', payload.query],
    queryFn: async () =>
      (
        await $httpRootApi.get<RequirementsBoardLaneResponse>(
          `/acquisitions/board/${payload.laneKey}${getQueriesAsSearch(payload.query)}`
        )
      ).data,
  }),
  fees: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/acquisitions/${id}/fees`)).data,
  }),
  show: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await requirementQueries.show(id)).data,
  }),
  transactions: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/acquisitions/${id}/transactions`)).data,
  }),
  availabilitySchedule: (payload: RequirementAvailabilityScheduleListPayload) => ({
    queryKey: [payload.id, payload.query],
    queryFn: async () =>
      (
        await $httpRootApi.get<RequirementAvailabilityScheduleListResponse>(
          `/acquisitions/${payload.id}/availability-schedule${getQueriesAsSearch(payload.query)}`
        )
      ).data,
  }),
  matchedProperties: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/acquisitions/${id}/matches`)).data,
  }),
  shortlist: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/acquisitions/${id}/shortlist`)).data,
  }),
  availabilityScheduleList: (id: string) => ({
    queryKey: [id],
    queryFn: async () => (await $httpRootApi.get(`/acquisitions/${id}/availability-schedule`)).data,
  }),
});

export type RequirementsQueryKeys = inferQueryKeys<typeof requirementsKeys>;

// ---- Queries ----

export const useGetRequirementsListQuery = (
  payload: RequirementsListPayload,
  options?: UseQueryOptions<RequirementsListResponse>
) =>
  useQuery<RequirementsQueryKeys['list']['queryKey'], RequirementsListResponse>({
    ...options,
    keepPreviousData: true,
    ...requirementsKeys.list(payload),
  });

export const useRequirementMatchedPropertiesQuery = (
  id: string,
  options?: UseQueryOptions<RequirementMatchesIndexResponse>
) =>
  useQuery<RequirementsQueryKeys['matchedProperties']['queryKey'], RequirementMatchesIndexResponse>({
    ...options,
    ...requirementsKeys.matchedProperties(id),
  });

export const useGetRequirementsAvailabilityScheduleListQuery = (
  id: string,
  options?: UseQueryOptions<RequirementAvailabilityScheduleListResponse>
) =>
  useQuery<RequirementsQueryKeys['availabilityScheduleList']['queryKey'], RequirementAvailabilityScheduleListResponse>({
    ...options,
    ...requirementsKeys.availabilityScheduleList(id),
  });

export const useGetRequirementsBoardQuery = (
  payload: RequirementsBoardPayload,
  options?: UseQueryOptions<RequirementsBoardResponse>
) =>
  useQuery<RequirementsQueryKeys['board']['queryKey'], RequirementsBoardResponse>({
    ...options,
    keepPreviousData: true,
    ...requirementsKeys.board(payload),
  });

export const useGetRequirementsBoardLaneQuery = (
  payload: RequirementsBoardLanePayload,
  options?: UseQueryOptions<RequirementsBoardLaneResponse>
) =>
  useQuery<RequirementsQueryKeys['boardLane']['queryKey'], RequirementsBoardLaneResponse>({
    ...options,
    enabled: false,
    keepPreviousData: true,
    ...requirementsKeys.boardLane(payload),
  });

export const useRequirementShowQuery = (id: string, options?: UseQueryOptions<RequirementShowItem>) =>
  useQuery<RequirementsQueryKeys['show']['queryKey'], RequirementShowItem>({
    ...requirementsKeys.show(id),
    ...options,
  });

export const useRequirementFeesQuery = (id: string, options?: UseQueryOptions<FeeShowItem>) =>
  useQuery<RequirementsQueryKeys['fees']['queryKey'], FeeShowItem>({
    ...requirementsKeys.fees(id),
    ...options,
  });

export const useRequirementTransactionsQuery = (
  id: string,
  options?: UseQueryOptions<RequirementTransactionsResponse>
) =>
  useQuery<RequirementsQueryKeys['transactions']['queryKey'], RequirementTransactionsResponse>({
    ...requirementsKeys.transactions(id),
    ...options,
  });

export const useRequirementShortlistQuery = (id: string, options?: UseQueryOptions<RequirementShortlistResponse>) =>
  useQuery<RequirementsQueryKeys['shortlist']['queryKey'], RequirementShortlistResponse>({
    ...requirementsKeys.shortlist(id),
    ...options,
  });

export const fetchMoreRequirements = async (key: RequirementsBoardLaneKey, params: RequirementsListParams) =>
  (await $httpRootApi.get<RequirementsBoardResponse>(`/acquisitions/board/${key}${getQueriesAsSearch(params)}`)).data;

// Note: Type-specific requirement queries are now implemented using
// useAxiosQuery pattern in components, calling requirementQueries functions directly

// ---- Mutations ----

export const useUpdateRequirementMutation = (
  opts?: UseMutationOptions<Partial<RequirementUpdatePayload>, RequirementShowItem, AxiosError<ErrorWithTrace>>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<RequirementUpdatePayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}`,
          data.body
        )
      ).data,
  });

export const useArchiveRequirementMutation = (
  opts?: UseMutationOptions<RequirementArchivePayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,

    mutationFn: async ({ id, ...data }) =>
      (
        await $httpRootApi.put<unknown, AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${id}/archive${getQueriesAsSearch(data)}`
        )
      ).data,
  });

export const useReassignRequirementMutation = (
  opts?: UseMutationOptions<RequirementReassignPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<RequirementReassignPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/reassign`,
          data.body
        )
      ).data,
  });

export const useRequirementRemoveTeamsMutation = (
  opts?: UseMutationOptions<RequirementRemoveTeamsPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (payload) =>
      (
        await $httpRootApi.put<unknown, AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${payload.id}/remove-teams${getQueriesAsSearch(payload.query)}`
        )
      ).data,
  });

export const useRequirementFeesUpdateMutation = (opts?: UseMutationOptions<FeeUpdatePayload, FeeShowItem>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<FeeUpdatePayload['body'], AxiosResponse<FeeShowItem>>(
          `acquisitions/${data.id}/fees`,
          data.body
        )
      ).data,
  });

export const useRequirementAssociateCompanyMutation = (
  opts?: UseMutationOptions<RequirementAssociateCompanyParams, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<undefined, AxiosResponse<RequirementShowItem>>(
          `/requirement/${data.requirementId}/companies/${data.companyId}`
        )
      ).data,
  });

export const useRequirementDisassociateCompanyMutation = (
  opts?: UseMutationOptions<RequirementAssociateCompanyParams, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<undefined, AxiosResponse<RequirementShowItem>>(
          `/requirement/${data.requirementId}/companies/${data.companyId}`
        )
      ).data,
  });

export const useRequirementAssociateContactMutation = (
  opts?: UseMutationOptions<RequirementAssociateContactParams, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<undefined, AxiosResponse<RequirementShowItem>>(
          `/requirement/${data.requirementId}/contacts/${data.contactId}`
        )
      ).data,
  });

export const useRequirementDisassociateContactMutation = (
  opts?: UseMutationOptions<RequirementAssociateContactParams, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<undefined, AxiosResponse<RequirementShowItem>>(
          `/requirement/${data.requirementId}/contacts/${data.contactId}`
        )
      ).data,
  });

export const useRequirementReorderContactsMutation = (
  opts?: UseMutationOptions<RequirementReorderContactsParams, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<
          Omit<RequirementReorderContactsParams, 'requirement_id'>,
          AxiosResponse<RequirementShowItem>
        >(`/acquisitions/${data.requirement_id}/contacts-order`, { contact_ids: data.contact_ids })
      ).data,
  });

export const useRequirementAddToShortlistMutation = (
  opts?: UseMutationOptions<RequirementAddToShortlistParams, RequirementAddToShortlistResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<
          Omit<RequirementAddToShortlistParams, 'requirement_id'>,
          AxiosResponse<RequirementAddToShortlistResponse>
        >(`/acquisitions/${data.requirement_id}/shortlist`, { letting_ids: data.letting_ids })
      ).data,
  });

export const useRequirementAddToLonglistMutation = (
  opts?: UseMutationOptions<RequirementAddToLonglistParams, RequirementAddToLonglistResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<
          Omit<RequirementAddToLonglistParams, 'requirement_id'>,
          AxiosResponse<RequirementAddToLonglistResponse>
        >(`/acquisitions/${data.requirement_id}/report-on`, { letting_ids: data.letting_ids })
      ).data,
  });

export const useRequirementApproveMutation = (opts?: UseMutationOptions<string, RequirementShowItem>) =>
  useMutation({
    ...opts,
    mutationFn: async (id) =>
      (await $httpRootApi.put<undefined, AxiosResponse<RequirementShowItem>>(`/acquisitions/${id}/approve`)).data,
  });

export const useRequirementReinstateMutation = (opts?: UseMutationOptions<string, RequirementShowItem>) =>
  useMutation({
    ...opts,
    mutationFn: async (id) =>
      (await $httpRootApi.put<undefined, AxiosResponse<RequirementShowItem>>(`/acquisitions/${id}/reinstate`)).data,
  });

export const useRequirementUpdateContactsMutation = (
  opts?: UseMutationOptions<RequirementUpdateContactsPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<RequirementUpdateContactsPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/contacts`,
          data.body
        )
      ).data,
  });

export const useRequirementDeleteContactsMutation = (
  opts?: UseMutationOptions<RequirementDeleteContactsPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<RequirementDeleteContactsPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/contacts/${data.contact_id}${getQueriesAsSearch(data.body)}`
        )
      ).data,
  });

export const useRequirementUpdateCompaniesMutation = (
  opts?: UseMutationOptions<RequirementUpdateCompaniesPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<RequirementUpdateCompaniesPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/companies`,
          data.body
        )
      ).data,
  });

export const useRequirementDeleteCompaniesMutation = (
  opts?: UseMutationOptions<RequirementDeleteCompaniesPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<RequirementDeleteCompaniesPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/companies/${data.company_id}${getQueriesAsSearch(data.body)}`
        )
      ).data,
  });

// ---- Requirement, Record transaction - Mutations ----

export const useRequirementRecordLettingTransactionMutation = (
  opts?: UseMutationOptions<RequirementRecordLettingTransactionPayload, ItemWithId>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) => {
      const formData = serializeBodyToFormData(data.body);

      return (
        await $httpRootApi.post<FormData, ItemWithId>(`/acquisitions/${data.id}/record-letting`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      ).data;
    },
  });

export const useRequirementRecordSaleTransactionMutation = (
  opts?: UseMutationOptions<RequirementRecordSaleTransactionPayload, ItemWithId>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) => {
      const formData = serializeBodyToFormData(data.body);

      return (
        await $httpRootApi.post<FormData, ItemWithId>(`/acquisitions/${data.id}/record-sale`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      ).data;
    },
  });

// ---- Files ----

export const useRequirementReorderFilesMutation = (
  opts?: UseMutationOptions<RequirementReorderFilesPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<RequirementReorderFilesPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/assets`,
          data.body
        )
      ).data,
  });

export const useRequirementDeleteFileMutation = (opts?: UseMutationOptions<RequirementDeleteFilePayload, undefined>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.delete<RequirementDeleteFilePayload, AxiosResponse<undefined>>(
          `/acquisitions/${data.id}/assets/${data.assetId}`
        )
      ).data,
  });

export const useRequirementRenameFileMutation = (opts?: UseMutationOptions<RequirementRenameFilePayload, undefined>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.put<RequirementRenameFilePayload['body'], AxiosResponse<undefined>>(
          `/acquisitions/${data.id}/assets/${data.assetId}`,
          data.body
        )
      ).data,
  });

export const useRequirementCreateCommentMutation = (
  opts?: UseMutationOptions<RequirementCreateCommentPayload, RequirementShowItem>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<RequirementCreateCommentPayload['body'], AxiosResponse<RequirementShowItem>>(
          `/acquisitions/${data.id}/comment`,
          data.body
        )
      ).data,
  });

export const useRequirementMatchesMutation = (
  opts?: UseMutationOptions<RequirementMatchesPayload, RequirementMatchesResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<RequirementMatchesPayload['body'], AxiosResponse<RequirementMatchesResponse>>(
          `/acquisitions/${data.id}/report-on`,
          data.body
        )
      ).data,
  });

export const useRequirementSendMatchesMutation = (
  opts?: UseMutationOptions<RequirementSendMatchesPayload, undefined>
) =>
  useMutation({
    ...opts,
    mutationFn: async (data) =>
      (
        await $httpRootApi.post<RequirementSendMatchesPayload['body'], AxiosResponse<undefined>>(
          `/acquisitions/${data.id}/shortlist/email`,
          data.body
        )
      ).data,
  });

// ---- Utils ----

export const updateRequirementShowCache = (id: string, callback: UpdateCacheCallback<RequirementShowItem>) =>
  updateQueryCache<RequirementShowItem>(requirementsKeys.show(id).queryKey, callback);

export const updateRequirementFeesShowCache = (id: string, callback: UpdateCacheCallback<FeeShowItem>) =>
  updateQueryCache<FeeShowItem>(requirementsKeys.fees(id).queryKey, callback);
